/*
 * Copyright 2026 Sony Corporation
 */

import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Camera } from '@babylonjs/core/Cameras/camera';
import type { BackgroundMaterial } from '@babylonjs/core/Materials/Background/backgroundMaterial';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';

/**
 * The sky dome is real geometry, not a box riding on the camera: ground
 * projection folds the lower half of the panorama onto a disc at y = 0, and the
 * shader reads world positions to do it. So the dome has to be big enough to
 * hold a camera that orbits out to 20x the scene's extent, and small enough to
 * stay inside a far plane set at 200x. Everything here is a multiple of the
 * extent for that reason — a 0.3 m tabletop and a 40 m stage both work out.
 */
export const SKY_EXTENTS = 100;

/**
 * Where the ground stops being ground and becomes a distant backdrop, as a
 * multiple of the scene's extent. The floor for a close camera.
 */
const GROUND_EXTENTS = 6;

/** How far beyond the camera the rim of that disc is kept, so it is never in shot. */
const GROUND_CAMERA_MARGIN = 2.5;

/**
 * Roughly where a panorama is shot from: a tripod, not the floor.
 *
 * Moves with `GROUND_EXTENTS` — the two are one shape, and raising the radius
 * alone flattens the projection until the sky smears.
 */
const GROUND_HEIGHT_EXTENTS = 0.9;

/**
 * Keeps the projected ground wider than the camera is far.
 *
 * Ground projection folds the panorama onto a disc, and standing outside
 * that disc is what makes a scene read as a bubble with its own horizon
 * drawn across it. The disc therefore cannot be a property of the scene:
 * the camera orbits out to twenty times the extent, and whatever the extent
 * is, the rim has to stay behind the viewer. Sizing it from the scene alone
 * only moves the distance at which the bubble appears.
 *
 * Held to the scene's own size when the camera is close, so a small scene
 * keeps a near horizon and the panorama is not stretched to nothing.
 *
 * Per frame, and so never in a `.babylon`: whatever renders a published scene
 * has to run this itself, or it gets Babylon's defaults and a sky projected
 * onto a disc sized for nothing in it.
 */
export const holdProjectedGround = (
  sky: BackgroundMaterial,
  dome: AbstractMesh,
  camera: Camera,
  extent: number
): void => {
  const centre = dome.position;
  const distance = Vector3.Distance(camera.globalPosition, new Vector3(centre.x, 0, centre.z));
  const radius = Math.max(extent * GROUND_EXTENTS, distance * GROUND_CAMERA_MARGIN);

  sky.projectedGroundRadius = radius;
  // Kept in proportion: raising the radius alone flattens the projection to a
  // grazing angle and the sky smears into streaks.
  sky.projectedGroundHeight = radius * (GROUND_HEIGHT_EXTENTS / GROUND_EXTENTS);
};

/** The extent a dome was fitted to, which is all a loaded scene has left of it. */
export const extentOfDome = (dome: AbstractMesh): number => dome.scaling.x / SKY_EXTENTS;
